import React from 'react';
import { Callout, DocHeading, DocTable, Shot } from './components';

function PageSettings({ navigate }) {
  const link = (id, label) => (
    <button onClick={() => navigate(id)} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">{label}</button>
  );

  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Settings</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        The <strong className="text-white">SETTINGS</strong> tab, at the end of the top bar, holds
        the things that are not about farming: the status panel you see with the menu closed, your
        menu keys, where your files live, whether the connection is healthy, crash reports, and how
        to remove the bot from the game. Most cards below the first one only show status &mdash;
        there is very little to configure.
      </p>

      <Shot
        name="settings-01-tab.png"
        tab="SETTINGS"
        caption="The SETTINGS tab: ON-SCREEN STATUS, KEYBINDS, CONNECTION, MULTI-CLIENT, DATA FOLDER and CRASH REPORTS on the left, ABOUT and UNLOAD on the right."
        shoot="menushot (stage settings): a SETTINGS fül nyugalmi állapotban, egy klienssel. Bal oszlop: ON-SCREEN STATUS (BE, List, Bottom left, 100%, Say what it is doing BE, a Shown chipek), KEYBINDS (INSERT / END), CONNECTION (zöld LoginSucceeded), MULTI-CLIENT (first - nothing to do), DATA FOLDER (a kitalált Player útvonal), CRASH REPORTS. Jobb oszlop: ABOUT és UNLOAD."
      />

      <DocHeading>On-Screen Status</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        With the menu closed, a small panel on the game screen shows what is switched on and what it
        is doing right now: the route and its lap, the fight, the party, loot, selling, potions and
        the rest. The header says <strong className="text-white">FARMING</strong>,{' '}
        <strong className="text-white">WAITING</strong>, <strong className="text-white">STOPPED</strong>{' '}
        or <strong className="text-white">READY</strong>, with the time of the current run. Purple
        means it is doing that right now, orange that it is waiting for something, red that it
        stopped and needs you. The panel never takes a click &mdash; the game gets the mouse &mdash;
        and it fades out when you open the menu.
      </p>

      <Shot
        name="settings-02-on-screen-status.png"
        tab="SETTINGS › ON-SCREEN STATUS (in the game)"
        caption="The List look in the bottom-left corner during a run: lap 1, 47:12 into the run."
        shoot="menushot (stage statushud + a teljes világ-stage, zárt menü, tickmod hud): a List kinézet bal alul a játék képén — MORTIS · FARMING 47:12, Route lap 1, Combat, Party 2 together, Loot…Essence, Auto restart in 1:23 h."
      />

      <DocTable
        headers={['Setting', 'What it does']}
        rows={[
          [<strong className="text-white">Show it when the menu is closed</strong>, 'Switches the panel on and off. On by default.'],
          [<strong className="text-white">Look</strong>, <><strong>List</strong>: one line per feature, grouped. <strong>Icons</strong>: one line with the main activity and a row of small tiles underneath; a dot in the tile&rsquo;s corner turns orange while it waits and red when it stopped. <strong>Badge</strong>: a single line (the main activity and how many features are on) that opens into the full list when you point at it.</>],
          [<strong className="text-white">Corner</strong>, 'Click one of the four corners. Each sits next to the game’s own panels rather than on top of them. The List starts in the bottom left, because a tall list in the bottom right would cover the party frames.'],
          [<strong className="text-white">Size</strong>, '80 to 150 %.'],
          [<strong className="text-white">Say what it is doing</strong>, 'The short text on the right of each line (lap 1, bag 7%, 37850 left). Off: only the names.'],
          [<strong className="text-white">Only while a route runs</strong>, 'Hides the panel while nothing runs. A run that stopped with an error still shows, so you see why.'],
          [<strong className="text-white">List switched-off features too</strong>, 'Shows the features that are off as well, dimmed. Off by default: only what is on is listed.'],
          [<strong className="text-white">Shown</strong>, 'One chip per feature. A filled chip is shown; click it to leave that feature out of the panel.'],
        ]}
      />

      <DocHeading>Keybinds</DocHeading>
      <DocTable
        headers={['Key', 'Default', 'What it does']}
        rows={[
          [<strong className="text-white">Open menu</strong>, 'INSERT', 'Shows and hides the menu. It cannot be cleared: clearing it puts it back to INSERT, so you can never lock yourself out of the menu.'],
          [<strong className="text-white">Unload</strong>, 'END', 'Removes the bot from the game immediately. It has to be injected again afterwards.'],
        ]}
      />
      <Callout type="info">
        <strong>To change a key</strong>, click the row, then press the key you want. ESC keeps the
        old key, DEL clears it. A bound key also has a small X next to it &mdash; clicking the X
        clears the key too.
      </Callout>
      <Callout type="warning">
        <strong>The Unload key does not ask.</strong> Unlike the UNLOAD button below, pressing it
        unloads the bot straight away. It only reacts while the game window is in front, so pressing
        END in another program does nothing to the game. If you use END in the game for something
        else, bind Unload to a different key or clear it.
      </Callout>

      <p className="text-sm text-gray-400 mb-4">
        Individual features have their own keys on their own tabs. Like Unload, these only react
        while the game window is the active window:
      </p>
      <DocTable
        headers={['Tab', 'Key', 'What it does']}
        rows={[
          [link('auto-loot', 'FARM › Loot'), <strong className="text-white">Toggle key</strong>, 'Switches Auto Loot on and off without opening the menu.'],
          [link('auto-gather', 'FARM › Gather'), <strong className="text-white">Toggle key</strong>, 'Switches Auto Gather on and off without opening the menu.'],
          [link('auto-sell', 'FARM › Sell'), <strong className="text-white">Sell key</strong>, 'Starts selling, even with Auto Sell switched off. Press it again to stop.'],
          [link('auto-melt', 'FARM › Melt'), <><strong className="text-white">Rune key</strong>, <strong className="text-white">Item key</strong>, <strong className="text-white">Gem key</strong>, <strong className="text-white">Cube key</strong></>, 'Start melting runes, items or gems, or opening cubes. Press the key again to stop.'],
          [link('fast-mount', 'TOOLS › Fast mount'), <strong className="text-white">Mount now</strong>, 'Mounts immediately, even with the FAST MOUNT card switched off.'],
        ]}
      />

      <DocHeading>Connection</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        If the connection to the game server drops, the bot presses the game&rsquo;s own reconnect
        for you. There is no switch for it &mdash; it only acts when the connection is actually
        broken. It first gives the game about 5 seconds to recover by itself, then asks it to
        reconnect, leaving at least 20 seconds between attempts, up to 8 attempts. Notifications
        tell you when it starts (<em>Connection lost - asking the game to reconnect.</em>) and when
        it succeeds (<em>Reconnected.</em>).
      </p>
      <p className="text-sm text-gray-400 mb-4">
        When everything is fine the card shows a single line. The other lines appear while a
        problem is being handled.
      </p>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Status</strong>, <>The game&rsquo;s connection state. Green <em>LoginSucceeded</em> is normal. Red (for example <em>ConnectionLost</em>) means the connection is down and the bot is handling it. Orange is an in-between state such as <em>Connecting</em>; orange <em>unknown</em> is normal on a loading screen.</>],
          [<strong className="text-white">Attempts</strong>, 'How many times it asked the game to reconnect, out of 8. Red when all 8 are used.'],
          [<strong className="text-white">Next try</strong>, <>Seconds until the next attempt, or <span className="text-red-400">gave up</span> after the last one. At that point reconnect by hand.</>],
          [<strong className="text-white">Target</strong>, <><em>main server</em> is normal. An address in orange means the game still points at a map server, which the bot clears before it reconnects.</>],
          [<strong className="text-white">Requests sent / Target reset</strong>, 'Counters that stay after the connection is back: how many reconnect requests went out, and how many times an old map server had to be cleared first.'],
          [<strong className="text-white">Delivery</strong>, <>Green is normal. Orange <em>waiting for the app frame</em> is fine for the first moment. Red <em>breakpoint not installed</em> means the bot cannot press reconnect on this PC.</>],
          [<strong className="text-white">Last note</strong>, 'The last thing the reconnect reported. Copy this line into a support ticket.'],
        ]}
      />
      <Callout type="warning">
        If a notification says <em>Auto-reconnect is disabled - the game&rsquo;s command layout is
        not what we expect. Reconnect by hand.</em>, a game patch changed something and the bot
        switched this off on purpose. Reconnect by hand until the bot is updated.
      </Callout>
      <p className="text-sm text-gray-400 mb-4">
        Restarting the whole game client when it cannot recover is a separate feature of the
        loader: see {link('auto-restart', 'Auto Restart')}.
      </p>

      <DocHeading>Multi-Client</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Two game clients on one PC fight over one of the game&rsquo;s map files, and the one reading
        it gets kicked out on a map load. Every client after the first gives itself its own copy.
        There is nothing to switch on &mdash; but the bot has to be injected into{' '}
        <strong className="text-white">every</strong> client for this to work.
      </p>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Client slot: first - nothing to do</strong>, 'This is the first (or only) client. Normal. The reminder under it says the same as above: inject the bot into a second client too.'],
          [<strong className="text-white">Client slot: 2</strong>, 'This is the second client, and it took its own slot. Normal. A third client shows 3, and so on.'],
          [<strong className="text-white">Map cache: own copy</strong>, 'It got its own copy of the map file. This is what makes two clients work.'],
          [<strong className="text-white">Redirected</strong>, 'How many times it used its own copy. Zero until the first map load.'],
          [<span className="text-red-400">Client slot: could not reserve one</span>, 'Multi-client did not come up for this client. Run one client at a time until it is sorted.'],
          [<span className="text-red-400">Map cache: hook failed</span>, <>Multi-client did not come up either, and the card warns <em>Two clients will clash on map load.</em> Run one client at a time until it is sorted.</>],
        ]}
      />

      <DocHeading>Data Folder</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Your routes, combat and farm profiles, settings and crash reports live here &mdash; normally{' '}
        <code className="code-inline">%LOCALAPPDATA%\MortisEngine</code>.{' '}
        <strong className="text-white">OPEN FOLDER</strong> opens it in Explorer, and the full path
        is shown under the button. This is the folder to back up if you have spent time building
        routes and profiles.
      </p>

      <DocHeading>Crash Reports</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The bot watches for crashes and writes a report when one happens. That file is what makes a
        crash fixable.
      </p>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Watching</strong>, <>Green <em>yes (all traps)</em>: crash reporting is fully active. <em>yes (partial)</em> still works; red <em>no</em> means crashes will not be recorded.</>],
          [<strong className="text-white">Reports this session</strong>, 'How many reports were written. Anything above zero is worth sending in.'],
          [<strong className="text-white">Exceptions seen</strong>, 'Errors the bot noticed that did not crash anything. A number here on its own is not a problem.'],
          [<strong className="text-white">Reports LOST</strong>, 'Something went wrong but no report could be written. Mention it in your ticket.'],
          [<strong className="text-white">Asserts dismissed</strong>, 'The game showed one of its error pop-ups and the bot closed it, so the game would not freeze on it. The last one is named in brackets.'],
          [<span className="text-orange-300">Another module keeps taking the exception filter - we put it back.</span>, 'Something else in the game keeps replacing the crash catcher, and the bot keeps putting its own back. Mention it in your ticket if a crash was not recorded.'],
          [<strong className="text-white">Last report</strong>, 'The file name of the newest report. It is in the data folder above.'],
        ]}
      />
      <Callout type="info">
        <strong>If the game crashed</strong>, send us the report file and the version number from
        the ABOUT card. Those two together are usually enough.
      </Callout>

      <DocHeading>About</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Version</strong>, 'The build you are running. Always quote this in a ticket.'],
          [<strong className="text-white">Build</strong>, 'The date the build was made.'],
          [<strong className="text-white">Modules / Saved settings</strong>, 'How many features are loaded and how many settings are stored.'],
          [<strong className="text-white">SAVE SETTINGS NOW</strong>, 'Writes your settings immediately. They are saved by themselves a moment after every change too — this is just for peace of mind before closing.'],
        ]}
      />

      <DocHeading>Unload</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">UNLOAD</strong> removes the bot from the running game. It
        asks once (<em>Sure? Reloading needs the injector.</em>) &mdash; press{' '}
        <strong className="text-white">YES, UNLOAD</strong> to go ahead or{' '}
        <strong className="text-white">CANCEL</strong> to keep it. The game keeps running normally
        afterwards; to get the bot back you have to inject again. Closing the MortisEngine loader
        unloads the bot from every game too (the loader asks first).
      </p>
      <Callout type="info">
        You do not need to unload before closing the game &mdash; just close it. Your settings are
        already saved a moment after every change.
      </Callout>
    </div>
  );
}

export default PageSettings;
